import { ErrorResponse } from '@/shared/types';
import { applyFastStart } from '@/shared/utils/mp4-faststart';
import {
  runS3UploadPreflight,
  UploadPreflightError,
} from '@/shared/utils/upload-preflight';
import { S3UploadError, type UploadErrorReason } from './s3-upload-error';
import type { UploadPreflightTarget } from './post-upload-preflight';
import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

/**
 * S3 응답 body에서 에러 코드를 추출합니다.
 * S3 XML 에러 응답 형식: <Error><Code>SignatureDoesNotMatch</Code><Message>...</Message></Error>
 */
const extractS3ErrorInfo = (responseData: unknown): string => {
  if (!responseData) return '';
  const body =
    typeof responseData === 'string'
      ? responseData
      : JSON.stringify(responseData);
  const codeMatch = body.match(/<Code>(.*?)<\/Code>/);
  const messageMatch = body.match(/<Message>(.*?)<\/Message>/);
  const parts: string[] = [];
  if (codeMatch?.[1]) parts.push(codeMatch[1]);
  if (messageMatch?.[1]) parts.push(messageMatch[1]);
  return parts.length > 0 ? parts.join(': ') : body.substring(0, 200);
};

const buildS3ErrorDetail = (error: unknown, file: File) => {
  const fileMeta = `파일: ${file.name}, 크기: ${(file.size / 1024 / 1024).toFixed(1)}MB, 타입: ${file.type || 'unknown'}`;

  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const code = error.code;
    const s3Error = extractS3ErrorInfo(error.response?.data);

    if (code === 'ERR_NETWORK') {
      return {
        summary: `S3 Network Error (${fileMeta}) - 네트워크 끊김 또는 CORS`,
        reason: 'NETWORK' as UploadErrorReason,
      };
    }
    if (code === 'ECONNABORTED') {
      return {
        summary: `S3 Timeout (${fileMeta})`,
        reason: 'TIMEOUT' as UploadErrorReason,
      };
    }
    if (code === 'ERR_CANCELED') {
      return {
        summary: `S3 Upload Canceled (${fileMeta})`,
        reason: 'UNKNOWN' as UploadErrorReason,
      };
    }
    if (status === 403) {
      return {
        summary: `S3 403 Forbidden (${fileMeta}) - ${s3Error || 'Presigned URL 만료 또는 서명 불일치'}`,
        reason: 'FORBIDDEN' as UploadErrorReason,
      };
    }
    return {
      summary: `S3 HTTP ${status ?? 'unknown'} [${code ?? 'no-code'}] (${fileMeta})${s3Error ? ` - ${s3Error}` : ''}`,
      reason: 'UNKNOWN' as UploadErrorReason,
    };
  }

  const msg = error instanceof Error ? error.message : String(error);
  return {
    summary: `S3 Upload Error: ${msg} (${fileMeta})`,
    reason: 'UNKNOWN' as UploadErrorReason,
  };
};

/** 진단 probe 전체 cap. preflight 내부 timeout(15초)을 그대로 기다리지 않기 위함. */
const DIAGNOSTICS_PROBE_CAP_MS = 6_000;
/** probe 가 던진 임의 예외 메시지 cap. */
const PROBE_ERROR_MESSAGE_CHARS = 80;
/** 진단을 수집하는 axios 에러 코드 (응답 자체가 없는 네트워크성 실패). */
const DIAGNOSTICS_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED'];

/** presigned URL 에서 host 만 추출. 쿼리스트링(=서명)은 절대 포함하지 않는다. */
const extractPresignedHost = (presignedUrl: string): string | undefined => {
  try {
    return new URL(presignedUrl).host;
  } catch {
    return undefined;
  }
};

/**
 * probe 를 6초 cap 으로 실행하고 결과 토큰을 반환한다.
 * probe 예외는 삼킨다 — 진단이 원래 업로드 에러를 덮어쓰면 안 된다.
 */
const runDiagnosticsProbe = async (
  runProbe: () => Promise<unknown>,
): Promise<string> => {
  let capTimer: ReturnType<typeof setTimeout> | undefined;

  try {
    await Promise.race([
      runProbe(),
      new Promise((_resolve, reject) => {
        capTimer = setTimeout(
          () =>
            reject(
              new Error(`probe cap exceeded (${DIAGNOSTICS_PROBE_CAP_MS}ms)`),
            ),
          DIAGNOSTICS_PROBE_CAP_MS,
        );
      }),
    ]);

    return 'PROBE_OK';
  } catch (error) {
    if (error instanceof UploadPreflightError) {
      return error.reason;
    }

    const message = error instanceof Error ? error.message : String(error);

    return `PROBE_ERROR:${message.slice(0, PROBE_ERROR_MESSAGE_CHARS)}`;
  } finally {
    if (capTimer !== undefined) clearTimeout(capTimer);
  }
};

interface CollectUploadDiagnosticsParams {
  /** axios 에러 코드. 네트워크성 실패일 때만 진단을 수집한다. */
  errorCode?: string;
  /** 업로드 시작 → 실패까지 경과 ms. */
  elapsedMs: number;
  /** 마지막 progress 이벤트의 loaded 바이트. 0 이면 연결 자체가 안 된 신호. */
  progressBytes: number;
  presignedUrl: string;
  online?: boolean;
  /** resolve = S3 도달 가능. 미지정 시 probe 생략. */
  runProbe?: () => Promise<unknown>;
}

/**
 * 네트워크성 업로드 실패의 사후 진단을 한 줄 문자열로 조립한다.
 * 예: `probe=PREFLIGHT_NETWORK_ERROR, progress=0B, elapsed=380ms, online=true, host=...`
 */
export const collectUploadDiagnostics = async ({
  errorCode,
  elapsedMs,
  progressBytes,
  presignedUrl,
  online,
  runProbe,
}: CollectUploadDiagnosticsParams): Promise<string | undefined> => {
  if (!errorCode || !DIAGNOSTICS_ERROR_CODES.includes(errorCode)) {
    return undefined;
  }

  const parts: string[] = [];

  if (runProbe) {
    parts.push(`probe=${await runDiagnosticsProbe(runProbe)}`);
  }

  parts.push(`progress=${progressBytes}B`);
  parts.push(`elapsed=${elapsedMs}ms`);

  if (online !== undefined) {
    parts.push(`online=${online}`);
  }

  const host = extractPresignedHost(presignedUrl);
  if (host) {
    parts.push(`host=${host}`);
  }

  return parts.join(', ');
};

interface UploadFileToS3Params {
  file: File;
  presignedUrl: string;
  contentType?: string;
  onProgress?: (progress: number) => void;
  /**
   * 지정 시, 네트워크성 실패 순간에 해당 버킷으로 preflight probe 를 강제 실행해
   * "S3 자체가 안 닿는지 / 본 업로드만 실패했는지" 를 진단에 남긴다.
   * 버킷이 불명확한 호출부는 미전달 (probe 생략, 나머지 진단은 그대로 수집).
   */
  diagnosticsTarget?: UploadPreflightTarget;
}

/**
 * 파일을 Pre-signed URL로 업로드하고 진행률 콜백을 호출합니다.
 * - MP4/MOV 비디오 파일은 업로드 전 fast-start 처리하여 스트리밍 성능을 개선합니다.
 */
export const uploadFileToS3Api = async ({
  file,
  presignedUrl,
  contentType,
  onProgress,
  diagnosticsTarget,
}: UploadFileToS3Params): Promise<void> => {
  let startedAt = Date.now();
  let lastLoadedBytes = 0;

  try {
    let uploadFile = file;
    try {
      uploadFile = await applyFastStart(file);
    } catch (e) {
      console.warn(
        '[mp4-faststart] fast-start 처리 실패, 원본 파일로 업로드:',
        e,
      );
    }

    startedAt = Date.now();
    const response = await axios.put(presignedUrl, uploadFile, {
      headers: {
        'Content-Type': contentType || file.type,
        // TODO: 백엔드 presigned URL 서명에 Cache-Control 미포함 → S3 오브젝트 메타데이터에 설정 안 됨
        // be-api AbstractAwsS3Service.putObjectPreSignRequest에 .contentCacheControl() 추가 필요
        'Cache-Control': 'public, max-age=259200',
      },
      onUploadProgress: (progressEvent) => {
        lastLoadedBytes = progressEvent.loaded;
        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          if (onProgress) {
            onProgress(percentCompleted);
          }
        } else {
          console.warn('Upload progress total size is unknown.');
          if (onProgress) {
            onProgress(0);
          }
        }
      },
    });

    if (onProgress) {
      onProgress(100);
    }

    return response.data;
  } catch (error) {
    const detail = buildS3ErrorDetail(error, file);
    console.error('[Upload] S3 업로드 실패:', detail);

    // 네트워크성 실패는 원인(도메인 차단 / CORS / 오프라인)이 구분되지 않으므로
    // 실패 순간에 진단을 수집해 실패 신고(errorMessage)에 세분류를 붙인다.
    const diagnostics = await collectUploadDiagnostics({
      errorCode: error instanceof AxiosError ? error.code : undefined,
      elapsedMs: Date.now() - startedAt,
      progressBytes: lastLoadedBytes,
      presignedUrl,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      runProbe: diagnosticsTarget
        ? () => runS3UploadPreflight({ target: diagnosticsTarget })
        : undefined,
    });

    throw new S3UploadError(detail.summary, detail.reason, { diagnostics });
  }
};

export const usePutUploadFileToS3 = () => {
  return useMutation<void, AxiosError<ErrorResponse>, UploadFileToS3Params>({
    mutationFn: uploadFileToS3Api,
  });
};
